// Import statique et pas dynamique : `js-confetti` est déjà dans le bundle (le tableau de
// bord s'en sert pour fêter les souscriptions du jour), donc le charger à la demande ne
// faisait économiser rien du tout — et un `import()` raté dans un `catch` silencieux, c'est
// une fête qui n'a jamais lieu sans qu'on sache pourquoi.
import JSConfetti from "js-confetti"

// La scène commune aux bornes du mode arcade : le plein écran, le bandeau de score, l'écran
// de fin, la touche Échap et le record.
//
// Les huit jeux n'ont donc à s'occuper que de leur jeu. C'est aussi ce qui garantit qu'ils
// se ferment tous de la même façon — un jeu qui laisse un overlay derrière lui, sur une
// plateforme d'assurance, ce n'est plus drôle du tout.
//
// Les records vivent dans le `localStorage` : par navigateur, jamais envoyés, et sans
// conséquence s'ils disparaissent. Tous les accès sont gardés — navigation privée, stockage
// bloqué, l'appel peut lever.

const el = (tag, className, text) => {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text != null) node.textContent = text
  return node
}

const BEST_PREFIX = "arcade:best:"

// Un record, ça se fête BEAUCOUP : cinq salves enchaînées, emojis et confettis mêlés, de
// plus en plus larges. Une seule salve passait inaperçue derrière le bandeau.
const BURSTS = [
  { emojis: ["🏆", "🕹️"], emojiSize: 62, confettiNumber: 26, delay: 0 },
  { confettiRadius: 6, confettiNumber: 260, delay: 160 },
  { emojis: ["🚀", "✨", "🎉", "🥇"], emojiSize: 46, confettiNumber: 40, delay: 420 },
  { confettiRadius: 5, confettiNumber: 200, delay: 760 },
  { emojis: ["🎊", "💥", "⭐"], emojiSize: 54, confettiNumber: 36, delay: 1100 },
]

let confettiCannon = null

// `js-confetti` crée sa propre toile en `z-index: 1000` — soit MILLE de moins que la scène
// d'arcade. Les confettis tombaient donc derrière un fond quasi opaque : ils partaient bien,
// on ne les voyait jamais. On lui fournit donc notre toile, au-dessus de tout.
const confettiCanvas = () => {
  const existing = document.querySelector(".arcade-confetti")
  if (existing) return existing

  const canvas = el("canvas", "arcade-confetti")
  canvas.setAttribute("aria-hidden", "true")
  document.body.appendChild(canvas)
  return canvas
}

const celebrate = () => {
  confettiCannon ||= new JSConfetti({ canvas: confettiCanvas() })

  BURSTS.forEach(({ delay, ...burst }) => {
    window.setTimeout(() => confettiCannon.addConfetti(burst), delay)
  })
}

// La palette de la scène (cf. `.arcade-stage` dans components/_arcade.scss), pour les jeux dessinés
// en canvas : ses variables CSS, que les skins du mode fun redonnent. Lue une fois par partie, pas
// à chaque image — `getComputedStyle` n'est pas gratuit à soixante images par seconde.
export function arcadePalette(element) {
  const style = getComputedStyle(element)
  const read = (name, fallback) => style.getPropertyValue(name).trim() || fallback
  const rgb = (name, fallback) => {
    const triplet = read(name, fallback)
    return (alpha = 1) => (alpha === 1 ? `rgb(${triplet})` : `rgba(${triplet}, ${alpha})`)
  }

  return {
    ink: rgb("--arcade-ink-rgb", "244, 241, 255"),
    glow: rgb("--arcade-glow-rgb", "167, 139, 250"),
    deep: rgb("--arcade-deep-rgb", "124, 58, 237"),
    hot: rgb("--arcade-hot-rgb", "250, 204, 21"),
    bricks: read("--arcade-bricks", "#a78bfa, #22d3ee, #84cc16, #facc15, #fb7185").split(/\s*,\s*/)
  }
}

export function readBest(slug) {
  try {
    return Number(window.localStorage.getItem(BEST_PREFIX + slug)) || 0
  } catch {
    return 0
  }
}

export function writeBest(slug, score) {
  if (score <= readBest(slug)) return false

  try {
    window.localStorage.setItem(BEST_PREFIX + slug, String(score))
  } catch {
    // Stockage indisponible : le record de la partie en cours reste affiché, il ne survivra
    // simplement pas au rechargement.
  }
  return true
}

export class ArcadeStage {
  // `labels` est le dictionnaire i18n posé par le serveur (cf. ArcadeHelper).
  // `scoresUrl` : où envoyer le score en fin de partie, pour le tableau des records.
  // `scoreUnit` : « € » pour le tape-taupe, où le score est une somme récupérée.
  // `onStop` : facultatif. Les jeux qui peuvent durer indéfiniment (2048 n'a ni vies ni
  // chronomètre) affichent alors un bouton « Arrêter » qui fige le score au lieu d'obliger à
  // fermer la borne — fermer, c'est perdre la partie sans l'enregistrer.
  constructor({ labels, scoresUrl, scoreUnit, onRestart, onClose, onStop }) {
    this.labels = labels || {}
    this.scoresUrl = scoresUrl
    this.scoreUnit = scoreUnit || ""
    this.onRestart = onRestart
    this.onClose = onClose
    this.onStop = onStop
    this.slug = this.labels.slug || "game"
    this.score = 0
  }

  open() {
    if (this.root) return

    this.root = el("div", "arcade-stage")
    this.root.setAttribute("role", "dialog")
    this.root.setAttribute("aria-modal", "true")
    this.root.setAttribute("aria-label", this.labels.title || "Arcade")

    const frame = el("div", "arcade-stage__frame")
    // Le cadre prend le focus plutôt que la croix : un jeu qui se pilote aux flèches ne doit
    // pas démarrer avec « Fermer » sous la barre d'espace.
    frame.tabIndex = -1
    frame.append(this.#bar(), this.#board(), this.#hint())
    this.root.append(frame, this.#overlay())

    this.onKeydown = (event) => {
      if (event.key === "Escape") this.close()
    }
    document.addEventListener("keydown", this.onKeydown)
    // Un jeu qui se joue aux flèches ne doit pas faire défiler la page derrière lui.
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    document.body.appendChild(this.root)
    this.setScore(0)
    this.root.querySelector(".arcade-stage__frame").focus()
    return this.boardElement
  }

  close() {
    if (!this.root) return

    document.removeEventListener("keydown", this.onKeydown)
    document.body.style.overflow = this.previousOverflow || ""
    this.root.remove()
    this.root = null
    if (typeof this.onClose === "function") this.onClose()
  }

  get board() {
    return this.boardElement
  }

  setScore(value) {
    this.score = value
    if (this.scoreElement) this.scoreElement.textContent = this.formatScore(value)
  }

  // Séparateurs de milliers : un score de tape-taupe est un montant, et « 12345 € » ne se lit
  // pas d'un coup d'œil au milieu d'une partie.
  formatScore(value) {
    const formatted = new Intl.NumberFormat(document.documentElement.lang || "fr").format(value)
    return this.scoreUnit ? `${formatted}\u00a0${this.scoreUnit}` : formatted
  }

  // Les jeux à vies (casse-brique, tape-taupe) les affichaient chacun dans leur coin, en
  // tout petit sur leur propre canevas : on ne les voyait pas, et perdre une balle passait
  // pour un bug. Elles vivent donc dans le bandeau, avec le score.
  setLives(remaining, total = remaining) {
    if (!this.livesElement) return

    this.livesElement.hidden = false
    this.livesElement.replaceChildren(
      ...Array.from({ length: total }, (_, index) => {
        const dot = el("span", "arcade-stage__life")
        if (index >= remaining) dot.classList.add("arcade-stage__life--spent")
        return dot
      })
    )
    this.livesElement.setAttribute("aria-label", `${this.labels.lives || ""} ${remaining}/${total}`)
  }

  setHint(text) {
    if (this.hintElement) this.hintElement.textContent = text || ""
  }

  // Écran de fin : le score, le record, et deux sorties. `message` est facultatif — c'est la
  // phrase propre au jeu (« le ballon est tombé »).
  finish({ score = this.score, message = "" } = {}) {
    const isBest = writeBest(this.slug, score)
    if (this.bestElement) this.bestElement.textContent = String(readBest(this.slug))
    this.#submitScore(score)

    this.overElement.innerHTML = ""
    this.overElement.classList.toggle("arcade-stage__over--record", isBest)
    this.overElement.append(
      el("p", "arcade-stage__over-title", this.labels.game_over || "Game over"),
      el("p", "arcade-stage__over-score", this.formatScore(score))
    )
    if (message) this.overElement.append(el("p", "arcade-stage__over-message", message))
    if (isBest) this.#celebrateRecord()

    const actions = el("div", "arcade-stage__over-actions")
    const replay = el("button", "btn btn-primary", this.labels.replay || "Replay")
    replay.type = "button"
    replay.addEventListener("click", () => {
      this.hideOver()
      if (typeof this.onRestart === "function") this.onRestart()
    })
    const quit = el("button", "btn btn-link", this.labels.quit || "Quit")
    quit.type = "button"
    quit.addEventListener("click", () => this.close())
    actions.append(replay, quit)
    this.overElement.append(actions)

    if (this.stopElement) this.stopElement.hidden = true
    this.overElement.hidden = false
    replay.focus()
  }

  // Un record, ça se fête bruyamment : un bandeau qui claque, une fanfare d'emojis, et des
  // confettis. C'est la seule chose de tout le mode fun qui a le droit d'en faire trop.
  //
  // Rien de tout ça ne se déclenche en mouvement réduit : le bandeau reste, il ne bouge pas.
  #celebrateRecord() {
    const banner = el("div", "arcade-stage__record")
    banner.append(
      el("span", "arcade-stage__record-flare", "🏆"),
      el("span", "arcade-stage__record-label", this.labels.new_best || ""),
      el("span", "arcade-stage__record-flare", "🏆")
    )
    this.overElement.append(banner)

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.root?.classList.add("arcade-stage--flash")
    window.setTimeout(() => this.root?.classList.remove("arcade-stage--flash"), 700)
    this.#glitter()
    celebrate()
  }

  // Les confettis de `js-confetti` tombent sur toute la page ; celle-ci scintille par-dessus
  // la scène elle-même, pour que l'écran de fin ne soit pas le seul endroit immobile.
  #glitter() {
    const layer = el("div", "arcade-stage__glitter")
    layer.setAttribute("aria-hidden", "true")

    for (let index = 0; index < 26; index += 1) {
      const spark = el("span", "arcade-stage__spark")
      spark.style.setProperty("--spark-x", `${Math.random() * 100}%`)
      spark.style.setProperty("--spark-delay", `${Math.random() * 1.2}s`)
      spark.style.setProperty("--spark-scale", (0.6 + Math.random() * 1.1).toFixed(2))
      layer.appendChild(spark)
    }

    this.overElement.appendChild(layer)
    window.setTimeout(() => layer.remove(), 2600)
  }

  hideOver() {
    if (this.stopElement) this.stopElement.hidden = false
    this.overElement.hidden = true
    this.overElement.classList.remove("arcade-stage__over--record")
    this.overElement.innerHTML = ""
  }

  // Deux mémoires : le record personnel reste dans le navigateur (instantané, et sans
  // conséquence s'il disparaît), le tableau des records vit en base. Le serveur ne garde que
  // le meilleur (cf. ArcadeScore.record!), on peut donc poster chaque fin de partie.
  //
  // La salle est prévenue APRÈS la réponse : c'est elle qui recharge le classement, et le
  // recharger avant l'enregistrement le montrerait périmé. Un envoi qui échoue ne doit pas
  // gâcher la partie — on prévient quand même, le classement sera juste inchangé.
  #submitScore(score) {
    const announce = () =>
      document.dispatchEvent(new CustomEvent("arcade:finished", { detail: { slug: this.slug, score } }))

    if (!this.scoresUrl || score <= 0) {
      announce()
      return
    }

    fetch(this.scoresUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? "",
      },
      body: JSON.stringify({ game: this.slug, score }),
    })
      .catch(() => {})
      .finally(announce)
  }

  #bar() {
    const bar = el("header", "arcade-stage__bar")
    const name = el("span", "arcade-stage__name", this.labels.title || "")

    const scores = el("span", "arcade-stage__scores")
    this.livesElement = el("span", "arcade-stage__lives")
    this.livesElement.hidden = true
    this.scoreElement = el("b", null, "0")
    this.bestElement = el("b", null, String(readBest(this.slug)))
    const score = el("span", "arcade-stage__score")
    score.append(el("span", null, this.labels.score || "Score"), this.scoreElement)
    const best = el("span", "arcade-stage__best")
    best.append(el("span", null, this.labels.best || "Best"), this.bestElement)
    scores.append(this.livesElement, score, best)

    const close = el("button", "arcade-stage__close", "×")
    close.type = "button"
    close.setAttribute("aria-label", this.labels.close || "Close")
    close.addEventListener("click", () => this.close())

    bar.append(name, scores, ...this.#stopButton(), close)
    return bar
  }

  // Volontairement SÉPARÉ de la croix : « Arrêter » enregistre le score et ouvre l'écran de
  // fin, « × » referme la borne sans rien garder. Les confondre ferait perdre une partie à
  // qui voulait juste la ranger.
  #stopButton() {
    if (typeof this.onStop !== "function") return []

    this.stopElement = el("button", "btn btn-sm btn-outline-light arcade-stage__stop", this.labels.stop || "Stop")
    this.stopElement.type = "button"
    this.stopElement.addEventListener("click", () => this.onStop())
    return [this.stopElement]
  }

  #board() {
    this.boardElement = el("div", "arcade-stage__board")
    return this.boardElement
  }

  #hint() {
    this.hintElement = el("footer", "arcade-stage__hint", this.labels.hint || "")
    return this.hintElement
  }

  #overlay() {
    this.overElement = el("div", "arcade-stage__over")
    this.overElement.hidden = true
    return this.overElement
  }
}

// Un overlay ne doit jamais entrer dans un snapshot Turbo — même leçon que le vortex.
document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll(".arcade-stage").forEach((node) => node.remove())
  document.body.style.overflow = ""
})
